import { Client, IMessage, StompSubscription } from "@stomp/stompjs";
import {
  ElectronicParticipation,
  GeneralMeetingVoteType,
  ReceivedPowerOfAttorney,
} from "./meetings/generalMeeting/generalMeetingDomain";
import { logDebug } from "./utils/log";
import { isProductionEnvironment, isTestEnvironment } from "./utils/environments";
import { IndividualVote } from "./dbot/common/commonGeneralMeetingDomain";
import {
  ElectronicGeneralMeeting,
  GeneralMeetingStatus,
  StatusName,
} from "./meetings/generalMeeting/electronicGeneralMeetingDomain";
import { CompanyRoleIdWithName } from "./dbot/common/commonDomain";

/* eslint-disable  @typescript-eslint/no-explicit-any */
const logWebSocketError = (frame: any) => {
  const hasErrorMessage = frame.headers && "message" in frame.headers;
  logDebug("frame: ", frame);
  if (hasErrorMessage) {
    logDebug("Broker reported error: " + (hasErrorMessage ? frame.headers["message"] : ""));
  }
  if (frame.body) {
    logDebug("Additional details: " + frame.body);
  }
};

const webSocketUrl = isProductionEnvironment()
  ? "wss://app.dcompany.no"
  : isTestEnvironment()
    ? "wss://test.dcompany.no"
    : `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}`;

const TOO_MANY_DENIED_SUBSCRIPTIONS = "Too many denied subscriptions";

export class WebSocketStore {
  client: Client;
  activeSubscriptions: TopicSubscription[] = [];

  constructor() {
    this.client = new Client({
      brokerURL: `${webSocketUrl}/api/ws-broker`,
      onConnect: () => {
        logDebug("Connected to broker: " + webSocketUrl);
        this.resubscribeToActiveSubscriptions();
      },
      reconnectDelay: 5000,
      debug: function (str) {
        logDebug("::", str);
      },
    });
    this.client.activate();
    this.client.onWebSocketError = logWebSocketError;
    this.client.onStompError = (frame) => {
      logWebSocketError(frame);
      if (frame.headers["message"] === TOO_MANY_DENIED_SUBSCRIPTIONS) {
        this.client.deactivate();
      }
    };
    this.client.onDisconnect = () => {
      logDebug("Disconnected from broker");
    };
  }

  unsubscribe(topic: TopicName) {
    const topicSubscription = this.activeSubscriptions.find((s) => s.topic === topic);
    if (topicSubscription) {
      topicSubscription.subscription.unsubscribe();
      this.activeSubscriptions = this.activeSubscriptions.filter((s) => s.topic !== topic);
    }
    if (!this.activeSubscriptions.length) {
      this.client.deactivate();
    }
  }

  resubscribeToActiveSubscriptions() {
    this.activeSubscriptions = this.activeSubscriptions.map((s) => {
      logDebug("resubscribing to topic: " + s.topic.split("/").pop());
      const subscription = this.client.subscribe(
        s.topic,
        (message) => s.onMessage(this.decode(message)),
        s.headers,
      );
      return { ...s, subscription };
    });
  }

  private decode(message: IMessage) {
    return JSON.parse(message.body);
  }

  subscribe(
    topic: TopicName,
    onMessage: (message: WebSocketMessage) => void,
    onSubscribed?: (subscription: SubscriptionContext) => void,
    headers?: ParticipantCredential,
  ) {
    if (!this.client.active) {
      this.client.activate();
    }
    const onReadyForSubscribe = () => {
      if (!this.client.connected) {
        logDebug("Not connected to broker, cannot subscribe to topic");
      }
      logDebug("Subscribing to topic: " + topic.split("/").pop());
      const subscription = this.client.subscribe(
        topic,
        (message) => onMessage(this.decode(message)),
        headers,
      );
      this.activeSubscriptions.push({ topic, onMessage, subscription, headers });
      const context: SubscriptionContext = {
        unsubscribe: () => {
          this.unsubscribe(topic);
        },
      };
      onSubscribed?.(context);
    };
    const maxAttempts = 1000;
    let attempts = 0;
    const subscribeIfConnected = () => {
      if (this.client.connected) {
        onReadyForSubscribe();
      } else {
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(subscribeIfConnected, 7);
        }
      }
    };
    subscribeIfConnected();
  }
}

type TopicSubscription = {
  topic: TopicName;
  onMessage: (message: WebSocketMessage) => void;
  subscription: StompSubscription;
  headers?: ParticipantCredential;
};

export type SubscriptionContext = {
  unsubscribe: () => void;
};

// union type for all websocket messages
export type WebSocketMessage = ShareholderGeneralMeetingMessage | AdminGeneralMeetingMessage;

// union type for all general meeting messages received by shareholder

export type ShareholderGeneralMeetingMessage =
  | AdminUpdatedMeetingStatus
  | AdminChangedMeetingChair
  | AdminMeetingAnnouncement
  | AdminVoted
  | ShareholderRegisteredAttendance
  | ShareholderRegisteredPowerOfAttorney
  | RevokedPoAForParticipant
  | ReceivedPoAForParticipant;

// union type for all general meeting messages received by admin
export type AdminGeneralMeetingMessage = ShareholderRegisteredAttendance | ShareholderVoted;

export type AdminUpdatedMeetingStatus = {
  type: "meeting-status-updated";
  projectId: string;
  orgNo: string;
  statusName: StatusName;
  status: GeneralMeetingStatus;
  decisionId?: string;
  meeting: ElectronicGeneralMeeting;
  meetingChair?: CompanyRoleIdWithName;
  coSigners?: CompanyRoleIdWithName[];
};

export type AdminChangedMeetingChair = {
  type: "meeting-chair-changed";
  projectId: string;
  orgNo: string;
  meetingChair?: CompanyRoleIdWithName;
  coSigners?: CompanyRoleIdWithName[];
};

export type AdminMeetingAnnouncement = {
  type: "meeting-announcement";
  projectId: string;
  orgNo: string;
  announcement: string;
};

export type ShareholderRegisteredPowerOfAttorney = {
  type: "shareholder-power-of-attorney";
  shareholderId: number;
  projectId: string;
};

export type ReceivedPoAForParticipant = {
  type: "received-poa-for-participant";
  projectId: string;
  name: string;
  receivedPowerOfAttorneys: ReceivedPowerOfAttorney[];
};

export type RevokedPoAForParticipant = {
  type: "revoked-poa-for-participant";
  projectId: string;
  revokerName: string;
  revokedNoticeId: string;
};

export type ShareholderRegisteredAttendance = {
  type: "shareholder-participation";
  shareholderId: number;
  projectId: string;
  participation?: ElectronicParticipation;
};

export type AdminVoted = {
  type: "admin-voted";
  projectId: string;
  orgNo: string;
  shareholderId: number;
  voteType: GeneralMeetingVoteType;
  decisionId?: string;
  participation: ElectronicParticipation;
};

export type ShareholderVoted = {
  type: "shareholder-voted";
  projectId: string;
  voteId: string;
  shareholderId: number;
  voteType: GeneralMeetingVoteType;
  decisionId?: string;
  vote?: IndividualVote;
};

export type TopicName =
  | `/topic/project/${string}/admin`
  | `/topic/project/${string}/participants`
  | `/topic/notice/${string}`;

export type ParticipantCredential = { "notice-id": string } | { "poa-recipient-id": string };

export type SubscribeToWebSocketMessagesEvent = {
  type: "subscribe-to-web-socket-messages";
  topic: TopicName;
  onMessage: (message: WebSocketMessage) => void;
  onSubscribed?: (subscription: SubscriptionContext) => void;
  headers?: ParticipantCredential;
};

export type UnsubscribeFromWebSocketMessagesEvent = {
  type: "unsubscribe-from-web-socket-messages";
  topics: TopicName[];
};
